import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

const bannerBg = '/images/aip/HR/hr-banner-bg.svg';

export default function HR() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const title = '十大应用-人力';
    const legend = '合规・智能・自动・高效';
    const note =
      '依托中大型企业 HR 管理实践，以 AI 技术重构人力管理，实现员工全生命周期自动化、合规风险精准防控、人才发展个性化，让 HR 聚焦战略，企业聚焦核心业务。';

    const noteTips: NoteTip[] = [
      { value: '-70%', label: 'HR事务处理周期' },
      { value: '-67%', label: 'HR岗位人力投入' }
    ];

    const src = bannerBg;

    return {
      src,
      mobileSrc: bannerBg,
      title,
      subheading: '',
      legend,
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, [isPc]);

  return (
    <div className="hr-page">
      <div className="aip-banner-wrap hr-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {/* 核心能力 */}
        <section className="section">
          <div className="container">
            <h2 className="section-title">核心能力</h2>
            <p className="section-subtitle feature-subtitle">HR全链路功能模块</p>
            <div className="feature-split">
              <div className="feature-list">
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">组织与人事管理</span>
                    <span className="feature-index">#1</span>
                  </div>
                  <p className="feature-desc">
                    支持矩阵、项目制与多层集团架构，组织可追溯、全景可视；入转调离线上闭环，适配法规与多种用工，合规高效。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">考勤与薪酬管理</span>
                    <span className="feature-index">#2</span>
                  </div>
                  <p className="feature-desc">
                    标准/综合/不定时工时与多方式打卡，加班调休自动联动；薪酬政策同步，自动算薪与奖酬，银企、报税及业财对接，精准高效。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">人才获取与发展</span>
                    <span className="feature-index">#3</span>
                  </div>
                  <p className="feature-desc">
                    简历解析与岗位匹配，招聘线上化，黑名单与人才画像控风险；在线学习与智能路径，培训档案自动归档，助力成长。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">绩效与能力评估</span>
                    <span className="feature-index">#4</span>
                  </div>
                  <p className="feature-desc">
                    敏捷绩效与OKR，目标制定到复盘全流程，结果可联动激励；职位能力模型与员工画像，盘点结合AI辅助人岗匹配。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">系统集成与数据对接</span>
                    <span className="feature-index">#5</span>
                  </div>
                  <p className="feature-desc">
                    对接OA、电子签、财务、报账、银企与报税；兼容钉钉、企微、飞书，数据一站流转、系统协同高效。
                  </p>
                </article>
              </div>
              <div className="feature-preview">
                <div className="feature-placeholder">
                  <img src="/images/aip/HR/core-capabilities.png" alt="核心能力配图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 革新体验 */}
        <section className="section">
          <div className="container">
            <h2 className="section-title">革新体验</h2>
            <p className="section-subtitle feature-subtitle">让人工智能重塑HR的工作</p>
            <div className="feature-split">
              <div className="feature-list">
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">AI 驾驶舱</span>
                    <span className="feature-index">#1</span>
                  </div>
                  <p className="feature-desc">
                    零思考直给答案，极简工作流，主动推送数据洞察与预警，让 HR 决策前置、少走弯路。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">HR 共享服务中心</span>
                    <span className="feature-index">#2</span>
                  </div>
                  <p className="feature-desc">
                    一站式服务入口，员工、主管、HR 自助办理，流程标准化，实现人力成本精细化管控。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">AI 员工服务助手</span>
                    <span className="feature-index">#3</span>
                  </div>
                  <p className="feature-desc">
                    全天候智能应答员工 HR 咨询，政策解读、流程指引、证明开具咨询全覆盖，实现内部 HR 答疑零人工介入，大幅释放 HR 事务性工作精力。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">劳务用工专属适配</span>
                    <span className="feature-index">#4</span>
                  </div>
                  <p className="feature-desc">
                    覆盖劳务派遣、实习生、灵活用工多模式，计时 / 计件 / 按天结算规则灵活切换，信息实时协同。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">创新型企业深度适配</span>
                    <span className="feature-index">#5</span>
                  </div>
                  <p className="feature-desc">
                    支持项目制、跨部门协作，组织形态可快速调整，全链路 AI 智能化，降低管理成本，提升团队效能。
                  </p>
                </article>
              </div>
              <div className="feature-preview">
                <div className="feature-placeholder">
                  <img src="/images/aip/HR/innovative-experience.png" alt="革新体验配图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 客户案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">客户案例</h2>
            <p className="section-subtitle feature-subtitle">以可验证的业务结果检验计划体系的价值</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">三力士 中国橡胶V带制造企业龙头</h3>
                    <p className="case-desc">以“生产制造”为核心，助力企业供应链整体数字化升级。</p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src="/images/case-01.png" alt="案例01配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>

              <a className="case-link" href="https://terminus.io/case-detail/12080" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 02</span>
                  <div className="case-body">
                    <h3 className="case-title">源氏木语 纯实木家居品类代表性企业</h3>
                    <p className="case-desc">全量业务数字化蜕变，打造家居行业数字化样板间。</p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src="/images/case-02.png" alt="案例02配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
